/* WP-10 timeline (§14.4.6): date column + icon circle + sentence. */
import type { ReactNode } from 'react';
import { CheckIcon, CollectIcon, CloseIcon, HumidorIcon, PlusIcon } from '../icons';
import type { PortalHistoryEntry } from '../api/types';
import { dayNumber, formatMonthKey, formatDate } from '../utils/dates';
import { cn } from '../utils/cn';

const KIND_ICON: Record<string, { icon: ReactNode; ok?: boolean }> = {
  DEPOSIT: { icon: <PlusIcon size={14} /> },
  COLLECTED: { icon: <CheckIcon size={14} />, ok: true },
  COLLECTION_ARRANGED: { icon: <CollectIcon size={14} /> },
  COLLECTION_CANCELLED: { icon: <CloseIcon size={14} /> },
  TRANSFER_IN: { icon: <PlusIcon size={14} /> },
  TRANSFER_OUT: { icon: <CloseIcon size={14} /> },
  ADJUSTMENT: { icon: <HumidorIcon size={14} /> },
};

export function TimelineEntry({
  date,
  dateLabel,
  icon,
  ok,
  children,
  secondary,
}: {
  date?: string;
  dateLabel?: ReactNode;
  icon?: ReactNode;
  ok?: boolean;
  children: ReactNode;
  secondary?: ReactNode;
}) {
  return (
    <div className="hp-timeline__entry">
      <span className="hp-timeline__date">
        {date ? (
          <>
            <span className="hp-num">{dayNumber(date)}</span>
            {dateLabel ?? formatDate(date, 'en', { month: 'short' })}
          </>
        ) : (
          dateLabel
        )}
      </span>
      <span className={cn('hp-timeline__icon', ok && 'hp-timeline__icon--ok')} aria-hidden>
        {icon ?? <HumidorIcon size={14} />}
      </span>
      <span>
        {children}
        {secondary ? <span className="hp-listrow__sub">{secondary}</span> : null}
      </span>
    </div>
  );
}

export function HistoryTimelineEntry({
  entry,
  sentence,
  locale,
  secondary,
}: {
  entry: PortalHistoryEntry;
  sentence: string;
  locale: string;
  secondary?: ReactNode;
}) {
  const meta = KIND_ICON[entry.type] ?? { icon: <HumidorIcon size={14} /> };
  return (
    <TimelineEntry
      date={entry.occurredAt}
      dateLabel={formatDate(entry.occurredAt, locale, { month: 'short' })}
      icon={meta.icon}
      ok={meta.ok}
      secondary={secondary}
    >
      {sentence}
    </TimelineEntry>
  );
}

export function MonthHeader({ monthKey, locale }: { monthKey: string; locale: string }) {
  return <h2 className="hp-timeline__month">{formatMonthKey(monthKey, locale)}</h2>;
}
